/**
 * Admin — Email Unsubscribers
 * ─────────────────────────────
 * Single-page console for managing the re-engagement campaign:
 *   • Live stats (candidates per stage, already-sent counts, opt-outs)
 *   • Manual opt-out form (paste email → 1-click opt-out)
 *   • Send-test form (preview the rendered email on any inbox)
 *   • Table of currently opted-out users with refresh
 *
 * Backend endpoints used (all under /api/admin/diagnostic):
 *   GET  /reengagement/stats
 *   GET  /reengagement/optout-list?limit=200
 *   POST /reengagement/optout?email=X
 *   POST /reengagement/send-test?email=X&stage=N&username=Y
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Mail, MailX, RefreshCw, Send, ShieldCheck,
  Users, Inbox, AlertCircle, CheckCircle2, Trash2, Eye,
} from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = getBackendUrl();

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('el-GR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// ── Stat card ────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, accent = 'fuchsia', testId }) => {
  const accents = {
    fuchsia: 'from-fuchsia-500/20 to-purple-600/20 border-fuchsia-400/30 text-fuchsia-200',
    blue:    'from-blue-500/20 to-indigo-600/20 border-blue-400/30 text-blue-200',
    amber:   'from-amber-500/20 to-orange-600/20 border-amber-400/30 text-amber-200',
    emerald: 'from-emerald-500/20 to-teal-600/20 border-emerald-400/30 text-emerald-200',
    rose:    'from-rose-500/20 to-red-600/20 border-rose-400/30 text-rose-200',
  };
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl p-5 bg-gradient-to-br ${accents[accent]} border backdrop-blur-sm`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
          <p className="text-3xl font-extrabold text-white mt-1 tabular-nums">
            {value ?? '—'}
          </p>
        </div>
        <Icon className="w-6 h-6 opacity-70" />
      </div>
    </div>
  );
};

// ── Toast (light) ────────────────────────────────────────────
const Toast = ({ type, message, onClose }) => {
  const styles = {
    success: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-100',
    error:   'bg-rose-500/15 border-rose-400/40 text-rose-100',
    info:    'bg-blue-500/15 border-blue-400/40 text-blue-100',
  };
  const Icon = type === 'success' ? CheckCircle2 : type === 'error' ? AlertCircle : Inbox;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 mb-4 ${styles[type] || styles.info}`}
      data-testid={`toast-${type}`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm leading-relaxed">{message}</div>
      <button
        onClick={onClose}
        className="text-current opacity-70 hover:opacity-100"
        aria-label="dismiss"
      >
        ×
      </button>
    </div>
  );
};

const EmailUnsubscribers = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [optoutList, setOptoutList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);

  // Form states
  const [optoutEmail, setOptoutEmail] = useState('');
  const [optoutBusy, setOptoutBusy] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStage, setTestStage] = useState(1);
  const [testUsername, setTestUsername] = useState('φίλε');
  const [testBusy, setTestBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 6000);
  }, []);

  // ── Fetchers ───────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API}/api/admin/diagnostic/reengagement/stats`).then((r) => r.json()),
        fetch(`${API}/api/admin/diagnostic/reengagement/optout-list?limit=500`).then((r) => r.json()),
      ]);
      if (statsRes?._diagnostic_ok) setStats(statsRes);
      if (listRes?._diagnostic_ok) setOptoutList(listRes.items || []);
    } catch (e) {
      showToast('error', `Σφάλμα φόρτωσης: ${e.message}`);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Actions ────────────────────────────────────────────────
  const handleOptout = async () => {
    const email = optoutEmail.trim();
    if (!email) {
      showToast('error', 'Δώσε ένα email για opt-out.');
      return;
    }
    setOptoutBusy(true);
    try {
      const res = await fetch(
        `${API}/api/admin/diagnostic/reengagement/optout?email=${encodeURIComponent(email)}`,
        { method: 'POST' }
      ).then((r) => r.json());

      if (res?._diagnostic_ok) {
        if (res.already_optedout) {
          showToast('info', `Ο χρήστης ${res.user?.email || email} ήταν ήδη opted-out.`);
        } else {
          showToast(
            'success',
            `Επιτυχία! Ο χρήστης ${res.user?.username || ''} (${res.user?.email || email}) δεν θα λαμβάνει άλλα emails.`
          );
        }
        setOptoutEmail('');
        await fetchAll();
      } else {
        showToast('error', res?.error || 'Δεν βρέθηκε χρήστης με αυτό το email.');
      }
    } catch (e) {
      showToast('error', `Σφάλμα: ${e.message}`);
    } finally {
      setOptoutBusy(false);
    }
  };

  const handleSendTest = async () => {
    const email = testEmail.trim();
    if (!email) {
      showToast('error', 'Δώσε διεύθυνση παραλήπτη.');
      return;
    }
    setTestBusy(true);
    try {
      const qs = new URLSearchParams({
        email,
        stage: String(testStage),
        username: testUsername || 'φίλε',
      });
      const res = await fetch(
        `${API}/api/admin/diagnostic/reengagement/send-test?${qs.toString()}`,
        { method: 'POST' }
      ).then((r) => r.json());

      if (res?._diagnostic_ok && res.sent) {
        showToast('success', `Test email στάλθηκε στο ${email} (stage ${testStage}).`);
      } else {
        showToast('error', res?.error || 'Η αποστολή απέτυχε.');
      }
    } catch (e) {
      showToast('error', `Σφάλμα: ${e.message}`);
    } finally {
      setTestBusy(false);
    }
  };

  const handleQuickOptout = async (email) => {
    if (!email) return;
    if (!window.confirm(`Επιβεβαίωση: να γίνει opt-out ο/η ${email};`)) return;
    setOptoutEmail(email);
    // Use a one-shot direct call to avoid waiting for state to commit
    setOptoutBusy(true);
    try {
      const res = await fetch(
        `${API}/api/admin/diagnostic/reengagement/optout?email=${encodeURIComponent(email)}`,
        { method: 'POST' }
      ).then((r) => r.json());
      if (res?._diagnostic_ok) {
        showToast('success', `Opt-out έγινε για ${email}.`);
        setOptoutEmail('');
        await fetchAll();
      } else {
        showToast('error', res?.error || 'Αποτυχία opt-out.');
      }
    } catch (e) {
      showToast('error', `Σφάλμα: ${e.message}`);
    } finally {
      setOptoutBusy(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D0C4B] via-[#1f0838] to-[#0f0420] text-white">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors text-sm"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Πίσω στο Admin
            </button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
                <MailX className="w-7 h-7 text-rose-300" />
                Email Unsubscribers
              </h1>
              <p className="text-sm text-purple-200/75 mt-1">
                Διαχείριση re-engagement campaign · manual opt-out από replies «ΑΠΕΓΓΡΑΦΗ»
              </p>
            </div>
          </div>

          <button
            onClick={fetchAll}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-fuchsia-500/15 hover:bg-fuchsia-500/25 border border-fuchsia-400/30 transition-colors text-sm disabled:opacity-50"
            data-testid="refresh-btn"
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Ανανέωση
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard
            icon={MailX}
            label="Opted-out"
            value={stats?.opted_out_users}
            accent="rose"
            testId="stat-optedout"
          />
          <StatCard
            icon={Users}
            label="Stage 1 sent"
            value={stats?.already_sent_by_stage?.[1]}
            accent="blue"
            testId="stat-stage1-sent"
          />
          <StatCard
            icon={Users}
            label="Stage 2 sent"
            value={stats?.already_sent_by_stage?.[2]}
            accent="fuchsia"
            testId="stat-stage2-sent"
          />
          <StatCard
            icon={Users}
            label="Stage 3 sent"
            value={stats?.already_sent_by_stage?.[3]}
            accent="amber"
            testId="stat-stage3-sent"
          />
          <StatCard
            icon={ShieldCheck}
            label="Quiet hours"
            value={stats?.in_quiet_hours_athens ? 'ΝΑΙ' : 'ΟΧΙ'}
            accent="emerald"
            testId="stat-quiet-hours"
          />
        </div>

        {/* Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Manual opt-out */}
          <div className="rounded-2xl border border-rose-400/25 bg-rose-500/5 p-6 backdrop-blur-sm">
            <div className="flex items-start gap-3 mb-4">
              <MailX className="w-6 h-6 text-rose-300 mt-0.5" />
              <div>
                <h2 className="text-lg font-bold">Χειροκίνητο Opt-out</h2>
                <p className="text-xs text-purple-200/70 mt-0.5">
                  Όταν κάποιος απαντήσει «ΑΠΕΓΓΡΑΦΗ» στο{' '}
                  <code className="px-1.5 py-0.5 rounded bg-white/10 text-rose-200">info@radiosolutionsgreece.com</code>,
                  βάλε εδώ το email του.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="user@example.com"
                value={optoutEmail}
                onChange={(e) => setOptoutEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleOptout()}
                disabled={optoutBusy}
                data-testid="optout-email-input"
                className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-rose-400/60 outline-none text-sm placeholder:text-purple-300/40"
              />
              <button
                onClick={handleOptout}
                disabled={optoutBusy || !optoutEmail.trim()}
                data-testid="optout-submit-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-white text-sm font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {optoutBusy
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
                Opt-out
              </button>
            </div>
          </div>

          {/* Send test */}
          <div className="rounded-2xl border border-blue-400/25 bg-blue-500/5 p-6 backdrop-blur-sm">
            <div className="flex items-start gap-3 mb-4">
              <Send className="w-6 h-6 text-blue-300 mt-0.5" />
              <div>
                <h2 className="text-lg font-bold">Στείλε Test Email</h2>
                <p className="text-xs text-purple-200/70 mt-0.5">
                  Στείλε δοκιμαστικά οποιοδήποτε stage σε ένα inbox για να δεις το rendering.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <input
                type="email"
                placeholder="προορισμός email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                disabled={testBusy}
                data-testid="test-email-input"
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-blue-400/60 outline-none text-sm placeholder:text-purple-300/40"
              />
              <input
                type="text"
                placeholder="όνομα χρήστη (π.χ. spyros)"
                value={testUsername}
                onChange={(e) => setTestUsername(e.target.value)}
                disabled={testBusy}
                data-testid="test-username-input"
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-blue-400/60 outline-none text-sm placeholder:text-purple-300/40"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={testStage}
                onChange={(e) => setTestStage(Number(e.target.value))}
                disabled={testBusy}
                data-testid="test-stage-select"
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-blue-400/60 outline-none text-sm"
              >
                <option value={1}>Stage 1 (30+ ημ.)</option>
                <option value={2}>Stage 2 (60+ ημ.)</option>
                <option value={3}>Stage 3 (90+ ημ.)</option>
              </select>
              <button
                onClick={handleSendTest}
                disabled={testBusy || !testEmail.trim()}
                data-testid="test-send-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {testBusy
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Send className="w-4 h-4" />}
                Αποστολή
              </button>
            </div>
            <a
              href={`${API}/api/admin/diagnostic/reengagement/preview?stage=${testStage}&username=${encodeURIComponent(testUsername || 'φίλε')}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-300 hover:text-blue-200 underline"
              data-testid="preview-link"
            >
              <Eye className="w-3.5 h-3.5" />
              Preview Stage {testStage} (JSON)
            </a>
          </div>
        </div>

        {/* Opt-out list */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-fuchsia-300" />
              <h2 className="text-lg font-bold">Λίστα Opted-out Χρηστών</h2>
              <span
                className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-purple-200/85"
                data-testid="optout-list-count"
              >
                {optoutList.length}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center text-purple-200/70 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Φόρτωση…
            </div>
          ) : optoutList.length === 0 ? (
            <div className="p-10 text-center text-purple-200/60 text-sm" data-testid="optout-empty-state">
              <Mail className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Δεν υπάρχει κανένας opted-out χρήστης ακόμη.</p>
              <p className="mt-1 text-xs">
                Όταν λάβεις απάντηση «ΑΠΕΓΓΡΑΦΗ», αντίγραψε το email και χρησιμοποίησε τη φόρμα παραπάνω.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/30 text-xs uppercase tracking-wider text-purple-200/70">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Username</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Opt-out στις</th>
                    <th className="px-4 py-3 text-left font-semibold">Πηγή</th>
                    <th className="px-4 py-3 text-left font-semibold">Last stage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {optoutList.map((u, i) => (
                    <tr
                      key={`${u.email || u.username}-${i}`}
                      data-testid={`optout-row-${i}`}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {u.username || '—'}
                      </td>
                      <td className="px-4 py-3 text-purple-100/85 font-mono text-xs">
                        {u.email || '—'}
                      </td>
                      <td className="px-4 py-3 text-purple-200/75 whitespace-nowrap">
                        {fmtDate(u.opted_out_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-purple-200/60">
                        {u.source || 'manual_admin_reply'}
                      </td>
                      <td className="px-4 py-3 text-xs text-purple-200/75">
                        {u.last_stage ? `Stage ${u.last_stage}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Help footer */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-purple-200/80">
          <p className="font-semibold text-white mb-2 flex items-center gap-2">
            <Inbox className="w-4 h-4" /> Πώς δουλεύει η ροή
          </p>
          <ol className="list-decimal list-inside space-y-1 text-xs leading-relaxed pl-1">
            <li>Ο χρήστης απαντά «ΑΠΕΓΓΡΑΦΗ» στο email re-engagement.</li>
            <li>
              Η απάντηση καταλήγει στο{' '}
              <code className="px-1 py-0.5 rounded bg-white/10 text-fuchsia-200">
                info@radiosolutionsgreece.com
              </code>
            </li>
            <li>Αντιγράφεις το email του και το κάνεις opt-out από την φόρμα παραπάνω.</li>
            <li>
              Το flag <code className="px-1 py-0.5 rounded bg-white/10 text-emerald-200">reengagement_optout=True</code>{' '}
              μπαίνει στον χρήστη και κανένα μελλοντικό stage δεν θα του σταλεί.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default EmailUnsubscribers;
